import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Cropper, CropperRef, CircleStencil, RectangleStencil, ImageRestriction } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import 'react-advanced-cropper/dist/themes/default.css';
import {
  X,
  RotateCcw,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  Upload,
  Check,
  Loader2,
  ImageIcon,
} from 'lucide-react';

/** 裁剪结果数据结构 */
export interface CropResult {
  /** 裁剪得到的 HTMLCanvasElement */
  canvas: HTMLCanvasElement;
  /** 裁剪生成的图片 Blob 对象 */
  blob: Blob;
  /** 裁剪生成的 Base64 Data URL */
  dataUrl: string;
  /** 封装好的 File 对象 */
  file: File;
}

/** 预设宽高比选项 */
export interface AspectRatioOption {
  label: string;
  value?: number;
}

export interface ImageCropModalProps {
  /** 弹窗显隐状态 */
  open: boolean;
  /** 关闭弹窗回调 */
  onClose: () => void;
  /** 待裁剪的图片源 (可以是 URL 字符串、File 对象、Blob 对象或 null) */
  image?: string | File | Blob | null;
  /** 确认裁剪后的回调函数 */
  onCrop: (result: CropResult) => void | Promise<void>;
  /** 弹窗标题 (默认: "裁剪图片") */
  title?: string;
  /** 弹窗副标题/提示说明 */
  description?: string;
  /** 初始宽高比 (例如 1 代表 1:1，16/9 代表 16:9，不设或 0 代表自由比例) */
  aspectRatio?: number;
  /** 是否开启圆形剪裁框 (常用于头像裁剪) */
  circular?: boolean;
  /** 是否显示比例切换工具栏 (默认 true) */
  allowRatioSelect?: boolean;
  /** 自定义比例选项列表 */
  ratioOptions?: AspectRatioOption[];
  /** 输出文件 MIME 类型 (默认: "image/png") */
  outputType?: 'image/png' | 'image/jpeg' | 'image/webp';
  /** 输出图片质量 0-1 (默认: 0.92) */
  outputQuality?: number;
  /** 输出文件文件名 (默认: "cropped-image.png") */
  outputFileName?: string;
  /** 确认按钮是否处于加载状态 */
  loading?: boolean;
  /** 最小裁剪宽度（像素，默认: 50） */
  minWidth?: number;
  /** 最小裁剪高度（像素，默认: 50） */
  minHeight?: number;
  /** 最大裁剪宽度（像素，可选） */
  maxWidth?: number;
  /** 最大裁剪高度（像素，可选） */
  maxHeight?: number;
}

const DEFAULT_RATIO_OPTIONS: AspectRatioOption[] = [
  { label: '自由', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:4', value: 3 / 4 },
];

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  onClose,
  image: initialImage,
  onCrop,
  title = '裁剪图片',
  description = '拖动缩放或旋转调整图片显示区域',
  aspectRatio: initialRatio,
  circular = false,
  allowRatioSelect = true,
  ratioOptions = DEFAULT_RATIO_OPTIONS,
  outputType = 'image/png',
  outputQuality = 0.92,
  outputFileName = 'cropped-image.png',
  loading: externalLoading = false,
  minWidth = 50,
  minHeight = 50,
  maxWidth,
  maxHeight,
}) => {
  const cropperRef = useRef<CropperRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 内部选中的图片（可由外部传入或内部上传）
  const [currentImageSrc, setCurrentImageSrc] = useState<string | null>(null);
  // 当前宽高比
  const [selectedRatio, setSelectedRatio] = useState<number | undefined>(initialRatio);
  // 确认按钮提交加载状态
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 解析并初始化图片源
  useEffect(() => {
    if (!initialImage) {
      setCurrentImageSrc(null);
      return;
    }

    if (typeof initialImage === 'string') {
      setCurrentImageSrc(initialImage);
    } else if (initialImage && typeof initialImage === 'object' && 'size' in initialImage) {
      const objectUrl = URL.createObjectURL(initialImage as Blob);
      setCurrentImageSrc(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [initialImage]);

  // 当外部 aspectRatio 发生变化时更新
  useEffect(() => {
    setSelectedRatio(initialRatio);
  }, [initialRatio]);

  // 重置状态
  const handleReset = () => {
    if (cropperRef.current) {
      cropperRef.current.reset();
    }
  };

  // 处理本地文件上传选择
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const objectUrl = URL.createObjectURL(file);
      setCurrentImageSrc(objectUrl);
    }
  };

  // 顺时针旋转 90°
  const handleRotateRight = () => {
    cropperRef.current?.rotateImage(90);
  };

  // 逆时针旋转 90°
  const handleRotateLeft = () => {
    cropperRef.current?.rotateImage(-90);
  };

  // 水平翻转
  const handleFlipHorizontal = () => {
    cropperRef.current?.flipImage(true, false);
  };

  // 垂直翻转
  const handleFlipVertical = () => {
    cropperRef.current?.flipImage(false, true);
  };

  // 放大
  const handleZoomIn = () => {
    cropperRef.current?.zoomImage(1.2);
  };

  // 缩小
  const handleZoomOut = () => {
    cropperRef.current?.zoomImage(0.8);
  };

  // 执行裁剪确认
  const handleConfirmCrop = async () => {
    if (!cropperRef.current) return;
    const canvas = cropperRef.current.getCanvas();
    if (!canvas) return;

    try {
      setIsSubmitting(true);
      await new Promise<void>((resolve, reject) => {
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject(new Error('生成图片失败'));
              return;
            }
            const dataUrl = canvas.toDataURL(outputType, outputQuality);
            const file = new File([blob], outputFileName, { type: outputType });

            try {
              await onCrop({ canvas, blob, dataUrl, file });
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          outputType,
          outputQuality
        );
      });
    } catch (error) {
      console.error('裁剪提交出错:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 模态框未打开时不渲染
  if (!open) return null;

  const isLoading = externalLoading || isSubmitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      {/* 隐藏的文件上传 input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* 弹窗主卡片 */}
      <div className="bg-white border border-gray-200/80 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden text-foreground-950 max-h-[90vh] transition-all">
        {/* 1. 顶部 Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
          <div>
            <h3 className="text-lg font-bold text-foreground-950 tracking-tight">{title}</h3>
            {description && (
              <p className="text-xs text-foreground-500 mt-0.5">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-foreground-400 hover:text-foreground-700 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 2. 中间 裁剪核心区域 */}
        <div className="relative bg-slate-100/80 flex-1 min-h-[360px] sm:min-h-[420px] h-[440px] flex items-center justify-center overflow-hidden border-b border-gray-100">
          {currentImageSrc ? (
            <Cropper
              ref={cropperRef}
              src={currentImageSrc}
              className="w-full h-[440px] bg-slate-100/80"
              stencilComponent={circular ? CircleStencil : RectangleStencil}
              stencilProps={{
                aspectRatio: selectedRatio,
                minWidth,
                minHeight,
                maxWidth,
                maxHeight,
              }}
              minWidth={minWidth}
              minHeight={minHeight}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              imageRestriction={ImageRestriction.fitArea}
            />
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-xl cursor-pointer bg-white hover:bg-gray-50/80 transition-all text-foreground-500 hover:text-foreground-800 group shadow-xs"
            >
              <div className="p-4 rounded-full bg-primary-50 group-hover:bg-primary-100/80 mb-3 transition-colors">
                <ImageIcon className="w-8 h-8 text-primary-500" />
              </div>
              <p className="text-sm font-medium text-foreground-700">点击或选择本地图片进行裁剪</p>
              <p className="text-xs text-foreground-400 mt-1">支持 PNG, JPG, WEBP 等常见格式</p>
            </div>
          )}
        </div>

        {/* 3. 工具栏与比例选择 */}
        {currentImageSrc && (
          <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3 text-xs">
            {/* 图像操作工具按钮 */}
            <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200/80 shadow-xs">
              <button
                type="button"
                onClick={handleRotateLeft}
                title="向左旋转 90°"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleRotateRight}
                title="向右旋转 90°"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <RotateCw className="w-4 h-4" />
              </button>

              <div className="w-px h-4 bg-gray-200 my-auto" />

              <button
                type="button"
                onClick={handleFlipHorizontal}
                title="水平镜像"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <FlipHorizontal className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleFlipVertical}
                title="垂直镜像"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <FlipVertical className="w-4 h-4" />
              </button>

              <div className="w-px h-4 bg-gray-200 my-auto" />

              <button
                type="button"
                onClick={handleZoomIn}
                title="放大"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleZoomOut}
                title="缩小"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <ZoomOut className="w-4 h-4" />
              </button>

              <div className="w-px h-4 bg-gray-200 my-auto" />

              <button
                type="button"
                onClick={handleReset}
                title="重置图像转换"
                className="p-2 text-foreground-600 hover:text-foreground-950 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {/* 比例选择切片 (仅当非圆形模式且 allowRatioSelect 时显示) */}
            {allowRatioSelect && !circular && (
              <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200/80 shadow-xs">
                {ratioOptions.map((opt) => {
                  const isActive = selectedRatio === opt.value;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setSelectedRatio(opt.value)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                        isActive
                          ? 'bg-primary-500 text-white shadow-xs'
                          : 'text-foreground-600 hover:text-foreground-950 hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 4. 底部 Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-white">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 text-xs text-foreground-700 hover:text-foreground-950 px-3.5 py-2 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 transition-colors cursor-pointer shadow-xs"
          >
            <Upload className="w-3.5 h-3.5 text-foreground-500" />
            <span>更换图片</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium text-foreground-700 hover:text-foreground-950 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition-colors cursor-pointer disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleConfirmCrop}
              disabled={!currentImageSrc || isLoading}
              className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 active:bg-primary-700 rounded-xl transition-all shadow-sm shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>处理中...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>确认裁剪</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
